import React, { useEffect, useState } from 'react';
import { ClockIcon, LinkIcon, LightningBoltIcon, AcademicCapIcon, CheckCircleIcon } from '@heroicons/react/solid';
import rs from 'text-readability';
import { Tooltip } from '@heroui/react';

interface ArticleMetadataProps {
  title: string;
  content: string;
  article: object;
  className?: string;
}

interface ReadabilityScores {
  fleschScore: number;
  fleschKincaidScore: number;
  automatedReadability: number;
  colemanLiau: number;
  smog: number;
  gunningFog: number;
}

// Function to interpret Flesch Reading Ease score
const interpretFleschScore = (score: number): string => {
  if (score >= 90) return 'Bardzo łatwy do zrozumienia (poziom 5 klasy szkoły podstawowej)';
  if (score >= 80) return 'Łatwy do zrozumienia (poziom 6 klasy szkoły podstawowej)';
  if (score >= 70) return 'Dość łatwy do zrozumienia (poziom 7 klasy szkoły podstawowej)';
  if (score >= 60) return 'Przeciętnie trudny (poziom 8-9 klasy)';
  if (score >= 50) return 'Dość trudny (poziom szkoły średniej)';
  if (score >= 30) return 'Trudny (poziom studiów)';
  return 'Bardzo trudny (poziom akademicki/specjalistyczny)';
};


// Function to get improvement tips based on various scores
const getImprovementTips = (scores: ReadabilityScores, wordCount: number): string[] => {
  const tips: string[] = [];

  // Word count tips
  if (wordCount < 100) {
    tips.push('Tekst jest bardzo krótki. Rozbuduj go, aby był bardziej wartościowy.');
  } else if (wordCount > 3000) {
    tips.push('Tekst jest długi. Rozważ podzielenie go na mniejsze sekcje.');
  }

  // Flesch score tips
  if (scores.fleschScore < 50) {
    tips.push('Tekst jest dość trudny. Używaj krótszych zdań i prostszych słów.');
  }

  // Gunning Fog tips
  if (scores.gunningFog > 12) {
    tips.push('Tekst może być zbyt skomplikowany dla przeciętnego czytelnika. Uprość słownictwo.');
  }

  // SMOG tips
  if (scores.smog > 12) {
    tips.push('Zbyt wiele złożonych wyrazów. Zastąp je prostszymi odpowiednikami.');
  }

  // If everything seems fine
  if (tips.length === 0) {
    tips.push('Tekst ma dobrą czytelność. Dobra robota!');
  }

  return tips;
};

const ArticleMetadata: React.FC<ArticleMetadataProps> = ({ title, content, article, className = '' }) => {
  const [readingTime, setReadingTime] = useState<number>(0);
  const [slug, setSlug] = useState<string>('');
  const [improvementTips, setImprovementTips] = useState<string[]>([]);
  const [readabilityLevel, setReadabilityLevel] = useState<string>('');
  const [readabilityScores, setReadabilityScores] = useState<ReadabilityScores | null>(null);

  // Helper function to detect if text is likely in English
  const isEnglishText = (text: string): boolean => {
    if (!text || text.length < 50) return false;

    // Count common English words vs Polish-specific characters
    const englishWords = ['the', 'and', 'is', 'in', 'to', 'of', 'that', 'for', 'it', 'with'];
    const polishChars = ['ą', 'ę', 'ś', 'ć', 'ż', 'ź', 'ó', 'ń', 'ł'];

    const lowerText = text.toLowerCase();
    let englishWordCount = 0;
    let polishCharCount = 0;

    englishWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'g');
      const matches = lowerText.match(regex);
      if (matches) englishWordCount += matches.length;
    });

    polishChars.forEach(char => {
      const regex = new RegExp(char, 'g');
      const matches = lowerText.match(regex);
      if (matches) polishCharCount += matches.length;
    });

    // If more English word matches than Polish char matches, likely English
    return englishWordCount > polishCharCount;
  };

  // Calculate reading time and slug whenever content or title changes
  useEffect(() => {
    // Calculate reading time (average reading speed: 200 words per minute)
    const wordCount = content.trim().split(/\s+/).length;
    const readingTimeMinutes = Math.ceil(wordCount / 200);
    setReadingTime(readingTimeMinutes);

    // Generate slug from title
    if (article && article.slug) {
      setSlug(article.slug);
    }

    // Analyze text complexity and provide tips
    if (content && content.trim() !== '') {
      try {
        // Check if content is in English
        const isEnglish = isEnglishText(content);

        // Basic calculations for all texts
        const wordCount = content.trim().split(/\s+/).length;

        if (isEnglish) {
          // Use text-readability package to calculate various readability scores
          const fleschScore = rs.fleschReadingEase(content);
          const fleschKincaidScore = rs.fleschKincaidGrade(content);
          const automatedReadability = rs.automatedReadabilityIndex(content);
          const colemanLiau = rs.colemanLiauIndex(content);
          const smog = rs.smogIndex(content);
          const gunningFog = rs.gunningFog(content);

          const scores: ReadabilityScores = {
            fleschScore,
            fleschKincaidScore,
            automatedReadability,
            colemanLiau,
            smog,
            gunningFog
          };

          setReadabilityScores(scores);

          // Set overall readability level based on Flesch score (most widely used)
          setReadabilityLevel(interpretFleschScore(fleschScore));

          // Generate improvement tips based on scores
          const tips = getImprovementTips(scores, wordCount);
          setImprovementTips(tips);
        } else {
          // For Polish text, provide basic advice based on word count only
          setReadabilityScores(null);
          setReadabilityLevel('');

          const tips: string[] = [];
          if (wordCount < 100) {
            tips.push('Tekst jest bardzo krótki. Rozbuduj go, aby był bardziej wartościowy.');
          } else if (wordCount > 3000) {
            tips.push('Tekst jest długi. Rozważ podzielenie go na mniejsze sekcje.');
          } else {
            tips.push('Wskaźniki czytelności są dostępne tylko dla tekstów w języku angielskim.');
            tips.push('Dla tekstów po polsku dostępne są podstawowe wskazówki dotyczące długości.');
          }

          setImprovementTips(tips);
        }
      } catch (error) {
        console.error('Error analyzing text readability:', error);
        setImprovementTips(['Nie udało się przeanalizować czytelności tekstu.']);
        setReadabilityLevel('');
      }
    }
  }, [title, content, article?.slug]);

  if (!title && !content) return null;

  return (
    <div className={`flex flex-col text-sm text-gray-600 ${className}`}>
      <div className='flex flex-row gap-6'>
        {readabilityScores && (
          <div className="flex items-center mb-1">
            <AcademicCapIcon className="h-4 w-4 mr-1 text-gray-500" />
            <span>Poziom czytelności: <span className="font-medium">{readabilityLevel}</span></span>
          </div>
        )}
        <div className="flex items-center mb-1">
          <ClockIcon className="h-4 w-4 mr-1 text-gray-500" />
          <span>{readingTime} min czytania</span>
        </div>
        {slug && (
          <div className="flex flex-row items-center mb-1">
            <Tooltip content={`/a/${slug}`}>
              <LinkIcon className="h-4 w-4 mr-1 text-gray-500" />
            </Tooltip>URL
          </div>
        )}
      </div>

      {readabilityScores && (
        <div className="flex flex-col mt-1 bg-gray-50 p-2 rounded">
          <div className="text-xs font-medium mb-1 text-gray-700">Readability metrics (English text):</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="flex items-center">
              <LightningBoltIcon className="h-3 w-3 mr-1 text-amber-500" />
              <span>Flesch: {readabilityScores.fleschScore.toFixed(1)}</span>
            </div>
            <div className="flex items-center">
              <LightningBoltIcon className="h-3 w-3 mr-1 text-amber-500" />
              <span>Flesch-Kincaid: {readabilityScores.fleschKincaidScore.toFixed(1)}</span>
            </div>
            <div className="flex items-center">
              <LightningBoltIcon className="h-3 w-3 mr-1 text-amber-500" />
              <span>Gunning Fog: {readabilityScores.gunningFog.toFixed(1)}</span>
            </div>
            <div className="flex items-center">
              <LightningBoltIcon className="h-3 w-3 mr-1 text-amber-500" />
              <span>SMOG: {readabilityScores.smog.toFixed(1)}</span>
            </div>
          </div>
        </div>
      )}

      {improvementTips.length > 0 && (
        <div className="flex flex-col mt-2 bg-blue-50 p-2 rounded">
          <div className="text-xs font-medium mb-1 text-blue-700">Wskazówki dotyczące czytelności:</div>
          {improvementTips.map((tip, index) => (
            <div key={index} className="flex items-start mt-0.5">
              <CheckCircleIcon className="h-3 w-3 mt-0.5 mr-1 text-blue-500 flex-shrink-0" />
              <span className="text-xs text-blue-800">{tip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleMetadata;